import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { workerApi, authApi } from '../services/api'
import Loading from '../components/Loading'
import ErrorMessage from '../components/ErrorMessage'
import EmptyState from '../components/EmptyState'
import StatusBadge from '../components/StatusBadge'
import { channelMap, formatDateTime, isOverdue } from '../utils/helpers'
import { compressImage, validateImageFile, validateImageSize, validateVideoFile, validateVideoSize, validateVideoCount, validatePhotoCount } from '../utils/imageCompress'
import { uploadToOss } from '../utils/ossUpload'
import type { Order, OrderBatch } from '../types'

export default function Worker() {
  const navigate = useNavigate()

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [workerName, setWorkerName] = useState('')

  // Login form
  const [loginPhone, setLoginPhone] = useState('')
  const [loginPassword, setLoginPassword] = useState('')

  // Work state
  const [currentView, setCurrentView] = useState<'scan' | 'orders' | 'detail'>('scan')
  const [trackingNo, setTrackingNo] = useState('')
  const [orders, setOrders] = useState<Order[]>([])
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null)
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [exceptionReason, setExceptionReason] = useState('')
  const [showExceptionModal, setShowExceptionModal] = useState(false)
  const [finishedPhotos, setFinishedPhotos] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Defect report state
  const [showDefectModal, setShowDefectModal] = useState(false)
  const [defectDescription, setDefectDescription] = useState('')
  const [defectConsequence, setDefectConsequence] = useState('')
  const [defectPhotos, setDefectPhotos] = useState<string[]>([])
  const [defectVideo, setDefectVideo] = useState<string | null>(null)
  const [defectVideoName, setDefectVideoName] = useState('')
  const defectFileInputRef = useRef<HTMLInputElement>(null)
  const defectVideoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const token = localStorage.getItem('worker_token')
    const savedName = localStorage.getItem('worker_name')
    if (token && savedName) {
      setIsLoggedIn(true)
      setWorkerName(savedName)
    }
  }, [])

  const handleLogin = async () => {
    if (!loginPhone.trim() || !loginPassword.trim()) {
      setError('请输入手机号和密码')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await authApi.workerLogin(loginPhone.trim(), loginPassword.trim()) as {
        token: string
        user: { id: string; name: string; phone: string }
      }
      localStorage.setItem('worker_token', res.token)
      localStorage.setItem('worker_name', res.user.name)
      setWorkerName(res.user.name)
      setIsLoggedIn(true)
      setLoginPhone('')
      setLoginPassword('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '登录失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('worker_token')
    localStorage.removeItem('worker_name')
    setIsLoggedIn(false)
    setWorkerName('')
    setCurrentView('scan')
    setCurrentOrder(null)
  }

  const scanOrder = async () => {
    const inputValue = trackingNo.trim()
    if (!inputValue) {
      setError('请扫描或输入快递单号')
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await workerApi.scan(inputValue)
      // 防御性处理：specs 可能是字符串（旧数据兼容）
      const order = data as Order
      if (typeof order.specs === 'string') {
        try {
          order.specs = JSON.parse(order.specs)
        } catch {
          order.specs = []
        }
      }
      if (!Array.isArray(order.specs)) {
        order.specs = []
      }
      setCurrentOrder(order)
      const actionableIndex = order.batches?.findIndex(b => b.status === 'shipped' || b.status === 'received' || b.status === 'processing')
      setSelectedBatchIndex(actionableIndex >= 0 ? actionableIndex : 0)
      setCurrentView('detail')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '扫描失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const receiveBatch = async () => {
    if (!currentOrder || selectedBatchIndex === null) return
    if (!window.confirm('确认签收该批次材料？签收后将开始加工流程。')) return
    setLoading(true)
    try {
      const data = await workerApi.receive(currentOrder.orderNo, selectedBatchIndex)
      setCurrentOrder(data as Order)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '签收失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const startProcessingBatch = async () => {
    if (!currentOrder || selectedBatchIndex === null) return
    if (!window.confirm('确认开始加工该批次？开始后将计算加工截止时间。')) return
    setLoading(true)
    try {
      const data = await workerApi.start(currentOrder.orderNo, selectedBatchIndex)
      setCurrentOrder(data as Order)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '操作失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const completeBatch = async () => {
    if (!currentOrder || selectedBatchIndex === null) return
    if (finishedPhotos.length === 0) {
      setError('请至少上传一张完工照片')
      return
    }
    if (!window.confirm('确认加工完成？完成后将通知客户确认地址。')) return
    setLoading(true)
    try {
      const data = await workerApi.complete(currentOrder.orderNo, selectedBatchIndex, { photos: finishedPhotos })
      setCurrentOrder(data as Order)
      setFinishedPhotos([])
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '操作失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const markBatchException = async () => {
    if (!currentOrder || !exceptionReason.trim() || selectedBatchIndex === null) return
    if (!window.confirm('确认标记异常？异常后该批次将暂停加工。')) return
    setLoading(true)
    try {
      const data = await workerApi.exception(currentOrder.orderNo, selectedBatchIndex, { reason: exceptionReason })
      setCurrentOrder(data as Order)
      setShowExceptionModal(false)
      setExceptionReason('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '操作失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleRecover = async () => {
    if (!currentOrder || selectedBatchIndex === null) return
    if (!window.confirm('确认恢复该批次？恢复后将回到"已签收"状态，可重新开始加工。')) return
    setLoading(true)
    try {
      const data = await workerApi.recover(currentOrder.orderNo, selectedBatchIndex)
      setCurrentOrder(data as Order)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '恢复失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const countError = validatePhotoCount(finishedPhotos.length, files.length)
    if (countError) {
      setError(countError)
      e.target.value = ''
      return
    }

    if (!currentOrder?.id) {
      setError('订单未选中')
      e.target.value = ''
      return
    }

    setError('')
    const newPhotos: string[] = []
    for (const file of Array.from(files)) {
      const validation = validateImageFile(file)
      if (validation) {
        setError(validation)
        continue
      }
      const sizeError = validateImageSize(file)
      if (sizeError) {
        setError(sizeError)
        continue
      }
      try {
        const compressed = await compressImage(file)
        const fileName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
        const uploaded = await uploadToOss(
          compressed.blob,
          fileName,
          { orderId: currentOrder.id, type: 'finished_photo' },
          { width: compressed.width, height: compressed.height },
        )
        newPhotos.push(uploaded.url)
        // 移动端 Canvas 内存释放缓冲
        await new Promise(r => setTimeout(r, 150))
      } catch (err) {
        const msg = err instanceof Error ? err.message : '上传失败'
        setError(`图片上传失败: ${msg}`)
      }
    }
    if (newPhotos.length > 0) {
      setFinishedPhotos((prev) => [...prev, ...newPhotos])
    }
    e.target.value = ''
  }

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const data = await workerApi.getOrders() as Order[]
      // 防御性处理：兼容 specs 为字符串的旧数据
      const normalized = data.map((order) => {
        if (typeof order.specs === 'string') {
          try {
            order.specs = JSON.parse(order.specs)
          } catch {
            order.specs = []
          }
        }
        if (!Array.isArray(order.specs)) {
          order.specs = []
        }
        return order
      })
      setOrders(normalized)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '加载失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const reportDefect = async () => {
    if (!currentOrder || selectedBatchIndex === null) return
    if (!defectDescription.trim() || !defectConsequence.trim()) {
      setError('请填写问题描述和后果说明')
      return
    }
    if (defectPhotos.length === 0 && !defectVideo) {
      setError('请至少上传一张照片或视频')
      return
    }
    if (!window.confirm('确认上报材料瑕疵？上报后将通知客户确认处理方案。')) return
    setLoading(true)
    try {
      const data = await workerApi.reportDefect(currentOrder.orderNo, selectedBatchIndex, {
        description: defectDescription,
        consequence: defectConsequence,
        photos: defectPhotos,
        video: defectVideo || undefined,
      })
      setCurrentOrder(data as Order)
      setShowDefectModal(false)
      setDefectDescription('')
      setDefectConsequence('')
      setDefectPhotos([])
      setDefectVideo(null)
      setDefectVideoName('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '上报失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleDefectMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    const files = e.target.files
    if (!files || files.length === 0) return

    if (!currentOrder?.id) {
      setError('订单未选中')
      e.target.value = ''
      return
    }

    setError('')

    if (type === 'image') {
      const countError = validatePhotoCount(defectPhotos.length, files.length)
      if (countError) {
        setError(countError)
        e.target.value = ''
        return
      }

      const newPhotos: string[] = []
      for (const file of Array.from(files)) {
        const validation = validateImageFile(file)
        if (validation) {
          setError(validation)
          continue
        }
        const sizeError = validateImageSize(file)
        if (sizeError) {
          setError(sizeError)
          continue
        }
        try {
          const compressed = await compressImage(file)
          const fileName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
          const uploaded = await uploadToOss(
            compressed.blob,
            fileName,
            { orderId: currentOrder.id, type: 'defect_photo' },
            { width: compressed.width, height: compressed.height },
          )
          newPhotos.push(uploaded.url)
          // 移动端 Canvas 内存释放缓冲
          await new Promise(r => setTimeout(r, 150))
        } catch (err) {
          const msg = err instanceof Error ? err.message : '上传失败'
          setError(`图片上传失败: ${msg}`)
        }
      }
      if (newPhotos.length > 0) {
        setDefectPhotos((prev) => [...prev, ...newPhotos])
      }
    } else {
      // Video upload
      const countError = validateVideoCount(defectVideo ? 1 : 0, files.length)
      if (countError) {
        setError(countError)
        e.target.value = ''
        return
      }
      const file = files[0]
      const validation = validateVideoFile(file)
      if (validation) {
        setError(validation)
        e.target.value = ''
        return
      }
      const sizeError = validateVideoSize(file)
      if (sizeError) {
        setError(sizeError)
        e.target.value = ''
        return
      }
      try {
        const uploaded = await uploadToOss(
          file,
          file.name,
          { orderId: currentOrder.id, type: 'defect_video' },
        )
        setDefectVideo(uploaded.url)
        setDefectVideoName(file.name)
      } catch (err) {
        const msg = err instanceof Error ? err.message : '上传失败'
        setError(`视频上传失败: ${msg}`)
      }
    }

    e.target.value = ''
  }

  const getBatchStatusText = (batch: OrderBatch) => {
    switch (batch.status) {
      case 'material_pending': return '待寄材料'
      case 'material_shipped': return '材料运输'
      case 'received': return '材料签收'
      case 'processing': return '正在加工'
      case 'completed': return '加工完成'
      case 'ready_to_ship': return '待发货'
      case 'shipped': return '已发货'
      case 'exception': return '制作异常'
      default: return batch.status
    }
  }

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => navigate('/')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            加工人员登录
          </h1>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card">
            <p style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 16, letterSpacing: '0.15em' }}>
              请输入管理员分配的手机号和密码
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>手机号</label>
                <input
                  type="tel"
                  value={loginPhone}
                  onChange={(e) => setLoginPhone(e.target.value)}
                  placeholder="请输入手机号"
                  className="input"
                  maxLength={11}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>密码</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="input"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
              {error && (
                <p style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>{error}</p>
              )}
              <button onClick={handleLogin} disabled={loading} className="btn-primary">
                {loading ? '登录中' : '登录'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Scan view (logged in)
  if (currentView === 'scan') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => navigate('/')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            扫码收件
          </h1>
          <button
            onClick={handleLogout}
            style={{ fontSize: 12, color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.15em' }}
          >
            退出
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>
                扫描快递单号
              </label>
              <span style={{ fontSize: 12, color: '#9a9a9a', letterSpacing: '0.15em' }}>
                {workerName}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={trackingNo}
                onChange={(e) => setTrackingNo(e.target.value)}
                placeholder="扫描或输入快递单号"
                className="input"
                style={{ flex: 1 }}
                onKeyDown={(e) => e.key === 'Enter' && scanOrder()}
              />
              <button onClick={scanOrder} disabled={loading} className="btn-primary" style={{ padding: '14px 16px' }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#9a9a9a', marginTop: 12, letterSpacing: '0.15em' }}>
              请扫描包裹上的快递条码，系统将自动匹配对应订单
            </p>
          </div>

          {error && <ErrorMessage message={error} onRetry={() => setError('')} />}
          {loading && <Loading />}

          <div style={{ marginTop: 32 }}>
            <button
              onClick={() => { setCurrentView('orders'); fetchOrders() }}
              className="card w-full"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 24px' }}
            >
              <div style={{
                width: 48,
                height: 48,
                border: '1px solid #dddddd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 12,
              }}>
                <svg width="24" height="24" fill="none" stroke="#9a9a9a" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em' }}>我的加工单</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Orders view
  if (currentView === 'orders') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => setCurrentView('scan')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            加工单列表
          </h1>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '24px' }}>
          {orders.length === 0 ? (
            <EmptyState title="暂无加工单" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map((order) => (
                <div
                  key={order.id}
                  onClick={() => { setCurrentOrder(order); setCurrentView('detail') }}
                  className="card"
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>{channelMap[order.channel]}</span>
                    <StatusBadge status={order.status} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>{order.orderNo}</div>
                  {order.processingDeadline && (
                    <div style={{ fontSize: 13, color: '#313131', marginTop: 8, letterSpacing: '0.15em', fontWeight: isOverdue(order.processingDeadline) ? 500 : 400 }}>
                      截止：{formatDateTime(order.processingDeadline)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Detail view
  if (!currentOrder) return null

  const selectedBatch = selectedBatchIndex !== null ? currentOrder.batches?.[selectedBatchIndex] : null

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
      <div className="nav-bar">
        <button onClick={() => setCurrentView('scan')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
          订单处理
        </h1>
        <div style={{ width: 20 }} />
      </div>

      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Order Info */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>{channelMap[currentOrder.channel]}</span>
            <StatusBadge status={currentOrder.status} />
          </div>
          <div style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 18, fontWeight: 400, color: '#313131', letterSpacing: '0.15em' }}>
            {currentOrder.orderNo}
          </div>
          {currentOrder.processingDeadline && (
            <div style={{ fontSize: 15, color: '#313131', marginTop: 8, letterSpacing: '0.15em' }}>
              加工截止：{formatDateTime(currentOrder.processingDeadline)}
            </div>
          )}
        </div>

        {/* Batch Selector */}
        {currentOrder.batches && currentOrder.batches.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 12, letterSpacing: '0.15em' }}>
              选择批次
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {currentOrder.batches.map((batch, index) => (
                <button
                  key={batch.id}
                  onClick={() => setSelectedBatchIndex(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    border: selectedBatchIndex === index ? '2px solid #313131' : '1px solid #dddddd',
                    backgroundColor: selectedBatchIndex === index ? '#ffffff' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div>
                    <span style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em' }}>
                      第{batch.batchIndex + 1}批{batch.batchName ? ` (${batch.batchName})` : ''}
                    </span>
                    {batch.trackingNo && (
                      <span style={{ fontSize: 13, color: '#9a9a9a', marginLeft: 8, letterSpacing: '0.15em' }}>
                        {batch.carrier} {batch.trackingNo}
                      </span>
                    )}
                  </div>
                  <span className={`status-tag ${
                    batch.status === 'completed' ? 'status-tag-done' :
                    batch.status === 'exception' ? 'status-tag-error' :
                    batch.status === 'processing' ? 'status-tag-active' :
                    'status-tag-pending'
                  }`}>
                    {getBatchStatusText(batch)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Specs */}
        <div className="card">
          <h3 style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 16, letterSpacing: '0.15em' }}>
            定制规格
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {currentOrder.specs?.map((spec, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, letterSpacing: '0.15em' }}>
                <span style={{ color: '#9a9a9a' }}>{spec.type}（{spec.material}）</span>
                <span style={{ color: '#313131' }}>×{spec.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Batch Actions */}
        {selectedBatch && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedBatch.status === 'shipped' && (
              <button onClick={receiveBatch} disabled={loading} className="w-full btn-primary">
                {loading ? '处理中' : '确认签收该批次'}
              </button>
            )}

            {selectedBatch.status === 'received' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button onClick={startProcessingBatch} disabled={loading} className="w-full btn-primary">
                  {loading ? '处理中' : '开始加工该批次'}
                </button>
                <button onClick={() => setShowDefectModal(true)} disabled={loading} className="w-full btn-secondary">
                  检查材料（发现问题）
                </button>
              </div>
            )}

            {selectedBatch.status === 'exception' && selectedBatch.defectDescription && (
              <div className="card" style={{ borderWidth: 2, borderColor: '#313131' }}>
                <h3 style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em', marginBottom: 12, fontWeight: 500 }}>
                  材料瑕疵已上报
                </h3>
                {selectedBatch.defectDescription && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>问题描述：</span>
                    <p style={{ fontSize: 14, color: '#313131', marginTop: 4, letterSpacing: '0.15em' }}>{selectedBatch.defectDescription}</p>
                  </div>
                )}
                {selectedBatch.defectConsequence && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>后果说明：</span>
                    <p style={{ fontSize: 14, color: '#313131', marginTop: 4, letterSpacing: '0.15em' }}>{selectedBatch.defectConsequence}</p>
                  </div>
                )}
                {selectedBatch.defectPhotos && selectedBatch.defectPhotos.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>瑕疵照片：</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {selectedBatch.defectPhotos.map((photo, i) => (
                        <img key={i} src={photo} alt="" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd' }} />
                      ))}
                    </div>
                  </div>
                )}
                {selectedBatch.defectVideoUrl && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>瑕疵视频：</span>
                    <video
                      src={selectedBatch.defectVideoUrl}
                      controls
                      style={{ width: '100%', marginTop: 8, border: '1px solid #dddddd' }}
                    />
                  </div>
                )}
                <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', marginTop: 8 }}>
                  {selectedBatch.defectStatus === 'pending'
                    ? '等待客户确认处理方案...'
                    : selectedBatch.defectStatus === 'accepted'
                    ? '客户已同意补寄，等待新材料到达'
                    : '客户同意继续加工'}
                </p>
                {selectedBatch.defectStatus === 'rejected' && (
                  <button onClick={startProcessingBatch} disabled={loading} className="w-full btn-primary" style={{ marginTop: 12 }}>
                    {loading ? '处理中' : '开始加工该批次'}
                  </button>
                )}
              </div>
            )}

            {selectedBatch.status === 'processing' && (
              <>
                <div className="card">
                  <h3 style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 16, letterSpacing: '0.15em' }}>
                    上传完工照片
                  </h3>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                  />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {finishedPhotos.map((photo, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={photo} alt="" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd' }} />
                        <button
                          onClick={() => setFinishedPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            width: 16,
                            height: 16,
                            backgroundColor: '#313131',
                            color: '#fff',
                            border: 'none',
                            fontSize: 10,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        width: 80,
                        height: 80,
                        border: '1px dashed #dddddd',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9a9a9a',
                        background: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      <span style={{ fontSize: 11, marginTop: 2, letterSpacing: '0.15em' }}>上传</span>
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => setShowExceptionModal(true)} className="flex-1 btn-secondary">
                    标记异常
                  </button>
                  <button onClick={completeBatch} disabled={loading} className="flex-1 btn-primary">
                    {loading ? '处理中' : '加工完成'}
                  </button>
                </div>
              </>
            )}

            {selectedBatch.status === 'completed' && (
              <div className="card" style={{ borderColor: '#313131' }}>
                <p style={{ fontSize: 15, textAlign: 'center', color: '#313131', letterSpacing: '0.15em', fontWeight: 500 }}>
                  该批次加工已完成
                </p>
              </div>
            )}

            {selectedBatch.status === 'exception' && (
              <div className="card" style={{ borderWidth: 2, borderColor: '#313131' }}>
                <p style={{ fontSize: 15, textAlign: 'center', color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
                  该批次异常：{selectedBatch.exceptionReason || '未说明'}
                </p>
                <button onClick={handleRecover} disabled={loading} className="w-full btn-primary">
                  {loading ? '处理中' : '恢复该批次'}
                </button>
              </div>
            )}
          </div>
        )}

        {error && <ErrorMessage message={error} onRetry={() => setError('')} />}
      </div>

      {/* Exception Modal */}
      {showExceptionModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 px-6"
          style={{ backgroundColor: 'rgba(49, 49, 49, 0.5)' }}
        >
          <div className="card w-full max-w-sm">
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 18, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              标记异常
            </h3>
            <textarea
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              placeholder="请描述异常情况..."
              className="input"
              style={{ height: 96, resize: 'none' }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                onClick={() => { setShowExceptionModal(false); setExceptionReason('') }}
                className="flex-1 btn-secondary"
              >
                取消
              </button>
              <button
                onClick={markBatchException}
                disabled={!exceptionReason.trim() || loading}
                className="flex-1 btn-primary"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defect Report Modal */}
      {showDefectModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 px-6"
          style={{ backgroundColor: 'rgba(49, 49, 49, 0.5)' }}
        >
          <div className="card w-full max-w-sm" style={{ maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 18, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              上报材料瑕疵
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', display: 'block', marginBottom: 8 }}>
                  问题描述 *
                </label>
                <textarea
                  value={defectDescription}
                  onChange={(e) => setDefectDescription(e.target.value)}
                  placeholder="请描述材料存在的问题，如破损、规格不符等..."
                  className="input"
                  style={{ height: 80, resize: 'none' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', display: 'block', marginBottom: 8 }}>
                  后果说明 *
                </label>
                <textarea
                  value={defectConsequence}
                  onChange={(e) => setDefectConsequence(e.target.value)}
                  placeholder="请说明该瑕疵会造成怎样的后果，如无法加工、影响品质等..."
                  className="input"
                  style={{ height: 80, resize: 'none' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', display: 'block', marginBottom: 8 }}>
                  上传照片（最多9张）
                </label>
                <input
                  type="file"
                  ref={defectFileInputRef}
                  onChange={(e) => handleDefectMediaUpload(e, 'image')}
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {defectPhotos.map((photo, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={photo} alt="" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd' }} />
                      <button
                        onClick={() => setDefectPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                        style={{
                          position: 'absolute',
                          top: -4, right: -4,
                          width: 16, height: 16,
                          backgroundColor: '#313131',
                          color: '#fff',
                          border: 'none',
                          fontSize: 10,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => defectFileInputRef.current?.click()}
                    style={{
                      width: 80, height: 80,
                      border: '1px dashed #dddddd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9a9a9a',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span style={{ fontSize: 11, marginTop: 2, letterSpacing: '0.15em' }}>上传</span>
                  </button>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em', display: 'block', marginBottom: 8 }}>
                  上传视频（可选，最多50MB，仅支持MP4/MOV）
                </label>
                <input
                  type="file"
                  ref={defectVideoInputRef}
                  onChange={(e) => handleDefectMediaUpload(e, 'video')}
                  accept="video/mp4,video/quicktime,video/x-m4v"
                  className="hidden"
                />
                {!defectVideo ? (
                  <button
                    onClick={() => defectVideoInputRef.current?.click()}
                    style={{
                      width: '100%',
                      padding: '20px 0',
                      border: '1px dashed #dddddd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9a9a9a',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span style={{ fontSize: 13, marginTop: 4, letterSpacing: '0.15em' }}>点击上传视频</span>
                  </button>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <video
                      src={defectVideo}
                      controls
                      style={{ width: '100%', maxHeight: 200, border: '1px solid #dddddd' }}
                    />
                    <button
                      onClick={() => { setDefectVideo(null); setDefectVideoName('') }}
                      style={{
                        position: 'absolute',
                        top: -4, right: -4,
                        width: 20, height: 20,
                        backgroundColor: '#313131',
                        color: '#fff',
                        border: 'none',
                        fontSize: 12,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                    <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: 4, letterSpacing: '0.15em' }}>{defectVideoName}</p>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                onClick={() => {
                  setShowDefectModal(false)
                  setDefectDescription('')
                  setDefectConsequence('')
                  setDefectPhotos([])
                  setDefectVideo(null)
                  setDefectVideoName('')
                }}
                className="flex-1 btn-secondary"
              >
                取消
              </button>
              <button
                onClick={reportDefect}
                disabled={!defectDescription.trim() || !defectConsequence.trim() || loading}
                className="flex-1 btn-primary"
              >
                {loading ? '上报中' : '确认上报'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
